import React from 'react';

import Avatar from '@atlaskit/avatar';

import { useDynamicString } from '../../hooks/useDynamicValue.tsx';
import type { A2UIComponentDefinition } from '../../types/A2UIComponents.types.tsx';
import type { A2UIImageProps, ImageFit } from '../../types/A2UIComponents.types.tsx';

const variantSizeMap: Record<string, number> = {
	icon: 24,
	smallFeature: 80,
	mediumFeature: 200,
	largeFeature: 400,
};

const fitToCss: Record<ImageFit, string> = {
	contain: 'contain',
	cover: 'cover',
	fill: 'fill',
	none: 'none',
	scaleDown: 'scale-down',
};

type Props = {
	definition: A2UIComponentDefinition;
};

export const A2UIImage = ({ definition }: Props): React.JSX.Element => {
	const def = definition as A2UIImageProps;
	const url = useDynamicString(def.url);
	const variant = def.variant;
	const fit = def.fit;
	const label = def.accessibility?.label ?? '';

	// Avatar variant
	if (variant === 'avatar') {
		return <Avatar src={url} name={label} size="medium" />;
	}

	// Sized variants
	const size = variant ? variantSizeMap[variant] : undefined;
	const style: React.CSSProperties = {};
	style.maxWidth = '100%';
	style.height = 'auto';
	style.display = 'block';
	if (size) {
		style.width = size;
		style.height = size;
	}
	if (fit) {
		style.objectFit = fitToCss[fit] as React.CSSProperties['objectFit'];
	}

	return <img src={url} alt={label} style={style} />;
};
